import { computeWinLossStats, groupBySession } from '../statsUtils';
import { computePerformanceSnapshot } from './performanceInsights';
import { computeAvoidableLossAnalytics } from './avoidableLossAnalytics';
import { cleanCreatedTimestamp } from './dateUtils';
import { isTapeReading } from './tradeHelpers';
import { normalizeTradeEnvValueForAnalytics } from './tradeEnvAnalytics';

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfLocalDay(value) {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatLocalDateKey(value) {
  const date = new Date(value);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function formatWeeklyReviewRange(start, end) {
  const sameYear = start.getFullYear() === end.getFullYear();
  const startLabel = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const endLabel = end.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(!sameYear ? { year: 'numeric' } : {}),
  });
  return `${startLabel}–${endLabel}, ${end.getFullYear()}`;
}

export function getPreviousCompletedWeek(referenceDate = new Date()) {
  const today = startOfLocalDay(referenceDate);
  const daysSinceMonday = (today.getDay() + 6) % 7;
  const currentMonday = new Date(today.getTime() - (daysSinceMonday * DAY_MS));
  const end = new Date(currentMonday.getTime() - 1);
  const start = startOfLocalDay(new Date(end.getTime() - (6 * DAY_MS)));
  return {
    start,
    end,
    startKey: formatLocalDateKey(start),
    endKey: formatLocalDateKey(end),
    weekKey: formatLocalDateKey(start),
    label: formatWeeklyReviewRange(start, end),
  };
}

// Reviews are released after the Friday 4:00 PM U.S. market close. Before then,
// show the preceding Monday-Friday trading week; afterward, include the current
// Monday-Friday trading week immediately.
export function getWeeklyReviewRange(referenceDate = new Date()) {
  const today = startOfLocalDay(referenceDate);
  const dayOfWeek = today.getDay();
  const isFridayAfterMarketClose = dayOfWeek === 5
    && (referenceDate.getHours() > 16
      || (referenceDate.getHours() === 16 && referenceDate.getMinutes() >= 0));

  const daysSinceMonday = (dayOfWeek + 6) % 7;
  const currentMonday = new Date(today.getTime() - (daysSinceMonday * DAY_MS));
  const end = isFridayAfterMarketClose
    ? new Date(currentMonday.getTime() + (4 * DAY_MS))
    : new Date(currentMonday.getTime() - (3 * DAY_MS));
  const start = new Date(end.getTime() - (4 * DAY_MS));

  return {
    start,
    end,
    startKey: formatLocalDateKey(start),
    endKey: formatLocalDateKey(end),
    weekKey: formatLocalDateKey(start),
    label: formatWeeklyReviewRange(start, end),
  };
}

export function getWeeklyReviewTradeDate(entry) {
  if (entry?.year != null && entry?.month != null && entry?.day != null) {
    const date = new Date(Number(entry.year), Number(entry.month) - 1, Number(entry.day));
    if (!Number.isNaN(date.getTime())) return startOfLocalDay(date);
  }
  if (entry?.created) {
    const date = new Date(cleanCreatedTimestamp(entry.created));
    if (!Number.isNaN(date.getTime())) return startOfLocalDay(date);
  }
  return null;
}

export function isWeeklyReviewTrade(entry) {
  if (!entry || entry.isDeposit || entry.isPayout || entry.isBalanceAdjustment) return false;
  if (entry.isResetExcluded || isTapeReading(entry)) return false;
  return entry.pnl !== undefined && entry.pnl !== null && entry.pnl !== '' && Number.isFinite(Number(entry.pnl));
}

export function filterTradesForCompletedWeek(entries = [], range = getWeeklyReviewRange()) {
  const startMs = startOfLocalDay(range.start).getTime();
  const endMs = startOfLocalDay(range.end).getTime();
  return entries.filter((entry) => {
    if (!isWeeklyReviewTrade(entry)) return false;
    const date = getWeeklyReviewTradeDate(entry);
    const time = date?.getTime();
    return Number.isFinite(time) && time >= startMs && time <= endMs;
  });
}

function summarizeTrade(trade) {
  if (!trade) return null;
  const date = getWeeklyReviewTradeDate(trade);
  return {
    id: trade.id || '',
    ticker: trade.tickerTraded || trade.ticker || 'Trade',
    title: trade.title || '',
    pnl: Number(trade.pnl) || 0,
    rr: Number.isFinite(Number(trade.rr)) ? Number(trade.rr) : null,
    dateKey: date ? formatLocalDateKey(date) : '',
  };
}

function buildDailyRows(trades) {
  const groups = new Map();
  trades.forEach((trade) => {
    const date = getWeeklyReviewTradeDate(trade);
    if (!date) return;
    const key = formatLocalDateKey(date);
    const current = groups.get(key) || { dateKey: key, date, pnl: 0, rr: 0, rrCount: 0, trades: 0 };
    current.pnl += Number(trade.pnl) || 0;
    if (Number.isFinite(Number(trade.rr))) {
      current.rr += Number(trade.rr);
      current.rrCount += 1;
    }
    current.trades += 1;
    groups.set(key, current);
  });
  return [...groups.values()]
    .map((row) => ({
      ...row,
      label: row.date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
    }))
    .sort((left, right) => left.date - right.date);
}

function buildStrongestSession(trades, beThreshold) {
  const groups = groupBySession(trades);
  const rows = Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({
      label,
      ...computePerformanceSnapshot(items, beThreshold),
    }))
    .sort((left, right) => right.totalPnl - left.totalPnl || right.count - left.count);
  return rows[0] || null;
}

export function buildTradeEnvironmentPatterns(trades = [], fieldDefinitions = [], beThreshold, minimumCount = 2) {
  if (trades.length < 3) return [];
  const patterns = [];
  (fieldDefinitions || [])
    .filter((field) => field?.enabled && field.id && field.id !== 'lossClassification' && field.fieldType !== 'text')
    .forEach((field) => {
      const groups = new Map();
      trades.forEach((trade) => {
        const value = normalizeTradeEnvValueForAnalytics(trade[field.id], field);
        if (!value || value === 'N/A') return;
        const list = groups.get(value) || [];
        list.push(trade);
        groups.set(value, list);
      });
      const best = [...groups.entries()]
        .filter(([, items]) => items.length >= minimumCount)
        .map(([value, items]) => {
          const stats = computeWinLossStats(items, beThreshold);
          return {
            fieldId: field.id,
            label: field.label,
            value,
            trades: items.length,
            pnl: stats.sum,
            winRate: stats.winRate,
          };
        })
        .sort((left, right) => right.trades - left.trades || right.pnl - left.pnl)[0];
      if (best) patterns.push(best);
    });
  return patterns
    .sort((left, right) => right.trades - left.trades || Math.abs(right.pnl) - Math.abs(left.pnl))
    .slice(0, 3);
}

function createSourceHash(trades, fieldDefinitions) {
  const enabledFields = (fieldDefinitions || [])
    .filter((field) => field?.enabled)
    .map((field) => ({ id: field.id, label: field.label || '', fieldType: field.fieldType || '' }))
    .sort((left, right) => String(left.id).localeCompare(String(right.id)));
  const fieldIds = enabledFields.map((field) => field.id);
  const tradeSource = [...trades]
    .sort((left, right) => String(left.id || '').localeCompare(String(right.id || '')))
    .map((trade) => JSON.stringify({
      id: trade.id || '',
      date: getWeeklyReviewTradeDate(trade)?.toISOString() || '',
      ticker: trade.tickerTraded || trade.ticker || '',
      title: trade.title || '',
      pnl: Number(trade.pnl) || 0,
      rr: Number.isFinite(Number(trade.rr)) ? Number(trade.rr) : null,
      session: trade.tradeSession || '',
      style: trade.tradingStyle || '',
      loss: trade.lossClassification || '',
      mindset: trade.mindsetExecution || null,
      environment: fieldIds.reduce((values, fieldId) => ({ ...values, [fieldId]: trade[fieldId] ?? null }), {}),
    }))
    .join('|');
  const source = `${JSON.stringify(enabledFields)}|${tradeSource}`;
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function buildWeeklyReview({
  account,
  entries = [],
  fieldDefinitions = [],
  beThreshold,
  referenceDate = new Date(),
  reflections = {},
} = {}) {
  const range = getWeeklyReviewRange(referenceDate);
  const trades = filterTradesForCompletedWeek(entries, range);
  if (trades.length === 0) return null;

  const snapshot = computePerformanceSnapshot(trades, beThreshold);
  const totalR = trades.reduce((sum, trade) => (
    Number.isFinite(Number(trade.rr)) ? sum + Number(trade.rr) : sum
  ), 0);
  const dailyRows = buildDailyRows(trades);
  const sortedByPnl = [...trades].sort((left, right) => Number(right.pnl || 0) - Number(left.pnl || 0));
  const lossAnalytics = computeAvoidableLossAnalytics(trades, beThreshold);

  return {
    version: 1,
    accountId: account?.id || '',
    accountName: account?.name || 'Account',
    weekKey: range.weekKey,
    weekLabel: range.label,
    startKey: range.startKey,
    endKey: range.endKey,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'local',
    generatedAt: new Date().toISOString(),
    sourceHash: createSourceHash(trades, fieldDefinitions),
    tradeIds: trades.map((trade) => trade.id).filter(Boolean),
    tradeCount: snapshot.count,
    totalPnl: snapshot.totalPnl,
    totalR: Math.round(totalR * 100) / 100,
    winRate: snapshot.winRate,
    expectancy: snapshot.expectancy,
    profitFactor: snapshot.profitFactor,
    bestDay: [...dailyRows].sort((left, right) => right.pnl - left.pnl)[0] || null,
    worstDay: [...dailyRows].sort((left, right) => left.pnl - right.pnl)[0] || null,
    strongestSession: buildStrongestSession(trades, beThreshold),
    bestTrade: summarizeTrade(sortedByPnl[0]),
    worstTrade: summarizeTrade(sortedByPnl[sortedByPnl.length - 1]),
    lossClassification: lossAnalytics.categories[0] || null,
    tradeEnvironmentPatterns: buildTradeEnvironmentPatterns(trades, fieldDefinitions, beThreshold),
    limitedSample: trades.length < 3,
    reflections: {
      worked: reflections.worked || '',
      needsWork: reflections.needsWork || '',
      nextFocus: reflections.nextFocus || '',
    },
  };
}

function money(value) {
  const number = Number(value) || 0;
  return `${number < 0 ? '-' : number > 0 ? '+' : ''}$${Math.abs(number).toFixed(2)}`;
}

export function buildWeeklyReviewNotebookContent(review) {
  if (!review) return '';
  const lines = [
    `${review.accountName} — ${review.weekLabel}`,
    '',
    'WEEKLY RESULT',
    `Net P&L: ${money(review.totalPnl)}`,
    `Total R: ${review.totalR > 0 ? '+' : ''}${review.totalR.toFixed(2)}R`,
    `Trades: ${review.tradeCount}`,
    `Win rate: ${review.winRate.toFixed(1)}%`,
  ];
  if (review.bestDay) lines.push(`Best day: ${review.bestDay.label} (${money(review.bestDay.pnl)})`);
  if (review.worstDay) lines.push(`Worst day: ${review.worstDay.label} (${money(review.worstDay.pnl)})`);
  if (review.strongestSession) lines.push(`Strongest session: ${review.strongestSession.label} (${money(review.strongestSession.totalPnl)})`);
  if (review.lossClassification) lines.push(`Most common avoidable loss: ${review.lossClassification.classification}`);
  if (review.tradeEnvironmentPatterns.length > 0) {
    lines.push('', 'TRADE ENVIRONMENT');
    review.tradeEnvironmentPatterns.forEach((pattern) => {
      lines.push(`${pattern.label}: ${pattern.value} — ${pattern.trades} trades, ${money(pattern.pnl)}`);
    });
  }
  lines.push(
    '',
    'WHAT WORKED',
    review.reflections.worked || '',
    '',
    'WHAT NEEDS WORK',
    review.reflections.needsWork || '',
    '',
    "NEXT WEEK'S FOCUS",
    review.reflections.nextFocus || ''
  );
  return lines.join('\n').trim();
}
